import type { FinancialYear } from "../types.js";
import {
  type IncomeComponents,
  IncomeComponentsSchema,
  adjustedTaxableIncome,
  incomeTestsRulesFor,
} from "../income-tests/index.js";
import type { Au13Input } from "./types.js";

/**
 * AU-13 income derivation layer (Item 1 follow-on).
 *
 * The shipped `calculateAu13` takes `input.familyIncome` as an ALREADY-DERIVED
 * combined family Adjusted Taxable Income (ATI). ATI IS an `income-tests`
 * derivation (taxable income + adjusted fringe benefits total [exempt-employer
 * RFBA un-grossed-up by the 0.53 factor, non-exempt RFBA at full value] +
 * reportable super + net investment loss + tax-free pensions/benefits + target
 * foreign income − child maintenance expenditure). Rather than re-implement
 * those rules, we route raw components through the shared engine so it stays
 * the single source of income truth and the exempt-only 0.53 RFBA fix can't
 * drift between products.
 *
 * Family ATI = the SUM of each partner's individually-derived ATI (Services
 * Australia combines the partners' adjusted taxable incomes), so we derive each
 * party separately then add.
 *
 * NON-SHIPPED integration helper — imports income-tests, feeds the shipped CCS
 * engine, does NOT mutate any shipped `au13/fyXXXX-XX.ts` (architecture rule #1).
 * Pure: components + assessment year in, derived income out.
 */

/**
 * Derive one party's Adjusted Taxable Income from raw components via the
 * income-tests engine. The assessment year selects the income-tests ruleset —
 * load-bearing here because the exempt-employer RFBA un-gross-up factor
 * (0.53 = 1 − FBT rate) is FY-varying, so ATI genuinely depends on the FY.
 */
export function deriveAu13Ati(
  components: IncomeComponents,
  assessmentYear: FinancialYear,
): number {
  const rules = incomeTestsRulesFor(assessmentYear);
  const c = IncomeComponentsSchema.parse(components);
  return adjustedTaxableIncome(c, rules);
}

/**
 * Derive the COMBINED family ATI from each party's raw components. A single
 * (non-partnered) family passes only `self`. The combined figure is what the
 * shipped CCS engine consumes as `familyIncome`.
 */
export function deriveAu13FamilyIncome(
  args: {
    self: IncomeComponents;
    partner?: IncomeComponents;
    assessmentYear: FinancialYear;
  },
): number {
  const self = deriveAu13Ati(args.self, args.assessmentYear);
  const partner =
    args.partner === undefined
      ? 0
      : deriveAu13Ati(args.partner, args.assessmentYear);
  return self + partner;
}

/**
 * Build a `calculateAu13` input from raw self/partner components, deriving the
 * combined family ATI via income-tests. Every other CCS input (hours, care
 * type, fee, activity, flags, child rank) passes through unchanged. The result
 * is fed to the shipped `calculateAu13` exactly as a hand-derived family income
 * would be — engine and worked examples must reconcile (see derive-income.test.ts).
 */
export function buildAu13InputFromComponents(
  args: {
    self: IncomeComponents;
    partner?: IncomeComponents;
    assessmentYear: FinancialYear;
  } & Omit<Au13Input, "familyIncome">,
): Au13Input {
  const { self, partner, assessmentYear, ...rest } = args;
  return {
    ...rest,
    familyIncome: deriveAu13FamilyIncome({ self, partner, assessmentYear }),
  };
}
